import {
  Alert,
  AlertDescription,
  AlertTitle,
  ApiErrorState,
  Badge,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormLayout,
  FormMessage,
  FormSection,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Textarea,
  toast,
  useMobileNavDrillTop,
} from '@avila-roffe/components-web';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle, Rocket } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { useApplications } from '../../hooks/use-applications';
import { usePageHeader } from '../../page-header/PageHeaderContext';
import { fmtDate, releaseVariant } from './components/agent-tab-utils';
import {
  useAgentDeployments,
  useAgentDetail,
  useAgentRelease,
  useDeployRelease,
} from './hooks/use-agent-detail';
import { currentActiveDeployment, deploymentDefaults } from './release-deployment-utils';

const schema = z.object({
  applicationSlug: z.string().min(1, 'Application is required'),
  environmentSlug: z.string().min(1, 'Environment is required'),
  notes: z.string(),
});

type FormValues = z.infer<typeof schema>;

function releaseBackPath(agentId: string | undefined) {
  return `/agents/${agentId ?? ''}?tab=releases`;
}

function deploymentBackPath(agentId: string | undefined) {
  return `/agents/${agentId ?? ''}?tab=deployments`;
}

export function DeployReleasePage() {
  const { id: agentId, releaseId } = useParams<{ id: string; releaseId: string }>();
  const navigate = useNavigate();
  const drillTop = useMobileNavDrillTop();

  const { data: agent, isLoading: agentLoading, error: agentError } = useAgentDetail(agentId);
  const {
    data: release,
    isLoading: releaseLoading,
    error: releaseError,
  } = useAgentRelease(agentId, releaseId);
  const { data: deployments = [], isLoading: deploymentsLoading } = useAgentDeployments(agentId);
  const { data: applications = [] } = useApplications();
  const deployRelease = useDeployRelease(agentId ?? '');

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      applicationSlug: '',
      environmentSlug: '',
      notes: '',
    },
  });

  useEffect(() => {
    if (!agent || !releaseId) return;
    form.reset(deploymentDefaults(deployments, releaseId, agent.appSlug));
  }, [agent, deployments, form, releaseId]);

  const applicationSlug = form.watch('applicationSlug');
  const environmentSlug = form.watch('environmentSlug');
  const selectedApplication = applications.find((app) => app.slug === applicationSlug);

  const environmentOptions = useMemo(() => {
    const values = new Set<string>();
    for (const env of selectedApplication?.environments ?? []) {
      if (env.slug) values.add(env.slug);
    }
    for (const deployment of deployments) {
      if (deployment.applicationSlug === applicationSlug && deployment.environmentSlug) {
        values.add(deployment.environmentSlug);
      }
    }
    if (environmentSlug) values.add(environmentSlug);
    if (values.size === 0) {
      values.add('local');
      values.add('dev');
      values.add('staging');
      values.add('production');
    }
    return [...values];
  }, [applicationSlug, deployments, environmentSlug, selectedApplication]);

  const replacingDeployment = currentActiveDeployment(
    deployments,
    applicationSlug.trim(),
    environmentSlug.trim(),
  );
  const canDeploy = release?.status === 'PUBLISHED';
  const isLoading = agentLoading || releaseLoading || deploymentsLoading;

  usePageHeader({
    title: release ? `Deploy ${release.name}` : 'Deploy Release',
    subtitle: agent ? `${agent.interface.displayName || agent.name} · @${agent.name}` : undefined,
    backPath: releaseBackPath(agentId),
    mobileOnBack: drillTop?.onBack,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-52 w-full" />
      </div>
    );
  }

  if (agentError || releaseError || !agent || !release || !releaseId) {
    return (
      <ApiErrorState
        message={agentError?.message ?? releaseError?.message ?? 'Release not found.'}
        className="my-8"
      />
    );
  }

  async function onSubmit(values: FormValues) {
    if (!agentId || !releaseId || !release) return;
    if (!canDeploy) {
      toast.error('Only published releases can be deployed');
      return;
    }

    try {
      await deployRelease.mutateAsync({
        dto: {
          agentId,
          promptReleaseId: releaseId,
          applicationSlug: values.applicationSlug.trim(),
          environmentSlug: values.environmentSlug.trim(),
          notes: values.notes.trim() || undefined,
        },
      });
      toast.success(
        replacingDeployment
          ? `${release.name} deployed — previous active deployment replaced`
          : `${release.name} deployed`,
      );
      navigate(deploymentBackPath(agentId));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to deploy release');
    }
  }

  return (
    <Form {...form}>
      <FormLayout
        actionsPosition="sticky"
        isDirty={form.formState.isDirty}
        className="px-4"
        cancelAction={{
          label: 'Cancel',
          type: 'button',
          onClick: () => navigate(releaseBackPath(agentId)),
        }}
        submitAction={{
          label: deployRelease.isPending ? 'Deploying...' : 'Deploy',
          type: 'button',
          disabled: deployRelease.isPending || !canDeploy,
          onClick: () => void form.handleSubmit(onSubmit)(),
          icon: <Rocket className="size-4" />,
        }}
      >
        <FormSection title="Release" density="compact" columns={1} asCard>
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-sm font-medium text-primary">{release.name}</span>
              <Badge variant={releaseVariant(release.status)} appearance="soft">
                {release.status}
              </Badge>
            </div>
            <div className="grid gap-2 text-sm text-muted-foreground sm:grid-cols-3">
              <div>
                <span className="block text-xs uppercase text-muted-foreground">Created</span>
                {fmtDate(release.createdAt)}
              </div>
              <div>
                <span className="block text-xs uppercase text-muted-foreground">Model</span>
                <span className="font-mono">
                  {release.snapshot?.modelAlias ?? agent.modelAlias}
                </span>
              </div>
              <div>
                <span className="block text-xs uppercase text-muted-foreground">Skills</span>
                {(release.snapshot?.skillRefs?.length ?? 0) > 0
                  ? `${release.snapshot?.skillRefs?.length} bound`
                  : 'None'}
              </div>
            </div>
            {release.notes && <p className="text-sm text-muted-foreground">{release.notes}</p>}
          </div>
        </FormSection>

        {!canDeploy && (
          <Alert variant="warning" appearance="soft" icon={<AlertTriangle className="size-4" />}>
            <AlertTitle>Publish this release first</AlertTitle>
            <AlertDescription>
              Draft and deprecated releases cannot be deployed. Publish the release before creating
              a deployment version.
            </AlertDescription>
          </Alert>
        )}

        {replacingDeployment && (
          <Alert variant="warning" appearance="soft" icon={<AlertTriangle className="size-4" />}>
            <AlertTitle>Active deployment will be replaced</AlertTitle>
            <AlertDescription>
              Deploying to {applicationSlug} / {environmentSlug} will deactivate the current active
              deployment for that target.
            </AlertDescription>
          </Alert>
        )}

        <FormSection title="Deployment Target" density="compact" columns={1} asCard>
          <FormField
            control={form.control}
            name="applicationSlug"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Application</FormLabel>
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    const app = applications.find((item) => item.slug === value);
                    const currentEnvStillExists = app?.environments?.some(
                      (env) => env.slug === form.getValues('environmentSlug'),
                    );
                    if (!currentEnvStillExists && app?.environments?.[0]?.slug) {
                      form.setValue('environmentSlug', app.environments[0].slug, {
                        shouldDirty: true,
                      });
                    }
                  }}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select application" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {applications.length === 0 && (
                      <SelectItem value={agent.appSlug}>{agent.appSlug}</SelectItem>
                    )}
                    {applications.map((app) => (
                      <SelectItem key={app.slug} value={app.slug}>
                        {app.displayName || app.name || app.slug}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="environmentSlug"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Environment</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select environment" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {environmentOptions.map((envSlug) => (
                      <SelectItem key={envSlug} value={envSlug}>
                        {envSlug}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        <FormSection title="Adjustments" density="compact" columns={1} asCard>
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Deployment notes</FormLabel>
                <FormControl>
                  <Textarea {...field} placeholder="Optional deployment notes" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="space-y-2">
            <p className="text-sm font-medium leading-none">Release ID</p>
            <p
              id="deployment-release-id"
              className="min-h-10 rounded-md border bg-muted px-3 py-2 font-mono text-sm text-muted-foreground"
            >
              {release.id}
            </p>
          </div>
        </FormSection>
      </FormLayout>
    </Form>
  );
}
